import * as React from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { $api, fetchClient, invalidate, orderingParam } from "./client";
import type { components } from "./schema";

export type PlanningPolicy = components["schemas"]["PlanningPolicy"];
export type PatchedPlanningPolicy =
  components["schemas"]["PatchedPlanningPolicyRequest"];

/** All planning policies: bounded reference data, unpaginated. */
export const usePlanningPolicies = ({
  search,
  ordering,
}: { search?: string; ordering?: string } = {}) =>
  $api.useQuery(
    "get",
    "/api/v1/planning-policies/",
    { params: { query: { search, ordering: orderingParam(ordering) } } },
    { staleTime: 5 * 60 * 1000 },
  );

export function usePlanningPolicyName(): (
  id: number | null | undefined,
) => string {
  const { data } = usePlanningPolicies();
  return React.useCallback(
    (id) => data?.find((c) => c.id === id)?.name ?? "",
    [data],
  );
}

type PolicyFields = Pick<
  PlanningPolicy,
  "name" | "regime" | "serviceLevel" | "lotSizingMethod" | "orderCoverageDays"
>;

const invalidatePolicies = (queryClient: ReturnType<typeof useQueryClient>) =>
  invalidate(queryClient, "/api/v1/planning-policies/");

export function useCreatePlanningPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: Pick<PolicyFields, "name" | "regime"> & Partial<PolicyFields>,
    ) => (await fetchClient.POST("/api/v1/planning-policies/", { body })).data!,
    onSuccess: () => invalidatePolicies(queryClient),
  });
}

export function useUpdatePlanningPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: { id: number } & Partial<PolicyFields>) =>
      (
        await fetchClient.PATCH("/api/v1/planning-policies/{id}/", {
          params: { path: { id } },
          body,
        })
      ).data!,
    onSuccess: () => invalidatePolicies(queryClient),
  });
}

/**
 * Hard-deletes a policy. A 409 with `error.code = 'policy_has_members'` means it
 * still has member planning configs; the caller shows "move members first".
 */
export function useDeletePlanningPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchClient.DELETE("/api/v1/planning-policies/{id}/", {
        params: { path: { id } },
      }),
    onSuccess: () => invalidatePolicies(queryClient),
  });
}
